import { CopyOutlined } from "@ant-design/icons";
import { Button } from "antd";
import React from "react";

type ApiSummary = {
  range: string;
  percent: number;
  count: number;
};

type RangeConfig = {
  key: string;
  color: string;
  label: string;
};

const rangesConfig: RangeConfig[] = [
  { key: "<100", color: "#1677ff", label: "<100" },
  { key: "100-109", color: "#52c41a", label: "100-109" },
  { key: "110-119", color: "#52c41a", label: "110-119" },
  { key: "120-129", color: "#13c2c2", label: "120-129" },
  { key: "130-139", color: "#faad14", label: "130-139" },
  { key: "140-149", color: "#fa8c16", label: "140-149" },
  { key: "150-169", color: "#ff4d4f", label: "150-169" },
  { key: ">170", color: "#a8071a", label: ">170" }
];

export const BpSummaryReport = ({
  bpSummaryData
}: {
  bpSummaryData: ApiSummary[];
}) => {
  if (!Array.isArray(bpSummaryData) || bpSummaryData.length === 0) return null;

  const summary = rangesConfig.map(config => {
    const api = bpSummaryData.find(r => r.range === config.key);

    return {
      ...config,
      percentage: api ? api.percent : 0,
      count: api?.count ?? 0
    };
  });

  const total = summary.reduce((sum, r) => sum + r.count, 0);

  const highestRange =
    summary.reduce((prev, curr) => (curr.count > prev.count ? curr : prev)) ??
    summary[0];

  // ---- COPY FUNCTION ----
  const handleCopy = () => {
    const formatted = `
Blood Pressure Summary
Total Readings: ${total}

Range      Percentage     Count
--------------------------------------------
${summary
  .map(
    r =>
      `${r.label.padEnd(20)} ${String(r.percentage + "%").padEnd(16)} ${
        r.count
      }`
  )
  .join("\n")}
`.trim();

    navigator.clipboard.writeText(formatted);
  };

  return (
    <div
      style={{
        borderRadius: 16,
        padding: 24,
        marginTop: 20,
        marginBottom: 20,
        background: "#ffffff",
        boxShadow: "0 4px 20px rgba(0,0,0,0.06)"
      }}
    >
      {/* Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginBottom: 20
        }}
      >
        <div>
          <div style={{ fontSize: 16, fontWeight: 600 }}>
            Blood Pressure Summary
          </div>
          <div style={{ fontSize: 12, color: "#888" }}>
            Total Readings: {total}
          </div>
        </div>

        <Button
          onClick={handleCopy}
          style={{
            border: "1px solid #d9d9d9",
            background: "#fff",
            borderRadius: 6,
            cursor: "pointer"
          }}
          size="small"
          icon={<CopyOutlined />}
        >
          Copy
        </Button>
      </div>

      {/* Ranges */}
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {summary.map(item => {
          const isDominant = item.key === highestRange.key;

          return (
            <div key={item.key}>
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: 13,
                  marginBottom: 6
                }}
              >
                <span style={{ color: item.color }}>{item.label}</span>
                <span>
                  {item.percentage}% ({item.count})
                </span>
              </div>

              <div
                style={{
                  height: 4,
                  background: "#f0f0f0",
                  borderRadius: 6,
                  overflow: "hidden"
                }}
              >
                <div
                  style={{
                    width: `${item.percentage}%`,
                    background: item.color,
                    height: "100%",
                    transition: "width 0.5s ease"
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
